import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api-client"
import type { QuoteTemplate, QuoteTemplateDetail, QuoteTemplateVariable } from "@/lib/types"

export function useQuoteTemplates() {
  return useQuery({
    queryKey: ["quote_templates"],
    queryFn: () => api.get<{ data: QuoteTemplate[] }>("/api/v1/quote_templates"),
  })
}

export function useQuoteTemplate(id: string | undefined) {
  return useQuery({
    queryKey: ["quote_templates", id],
    queryFn: () => api.get<{ data: QuoteTemplateDetail }>(`/api/v1/quote_templates/${id}`),
    enabled: !!id,
  })
}

export function useCreateQuoteTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post<{ data: QuoteTemplateDetail }>("/api/v1/quote_templates", data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quote_templates"] }),
  })
}

export function useUpdateQuoteTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.patch<{ data: QuoteTemplateDetail }>(`/api/v1/quote_templates/${id}`, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quote_templates"] }),
  })
}

export function useDeleteQuoteTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/quote_templates/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quote_templates"] }),
  })
}

export function useSetDefaultQuoteTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<{ data: QuoteTemplate }>(`/api/v1/quote_templates/${id}/set_default`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quote_templates"] }),
  })
}

export function useDuplicateQuoteTemplate() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<{ data: QuoteTemplateDetail }>(`/api/v1/quote_templates/${id}/duplicate`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["quote_templates"] }),
  })
}

export function useQuoteTemplateVariables() {
  return useQuery({
    queryKey: ["quote_template_variables"],
    queryFn: () => api.get<{ data: QuoteTemplateVariable[] }>("/api/v1/quote_templates/variables"),
  })
}
